import { Controller } from "@hotwired/stimulus";

class SiteStatisticsController extends Controller {
  static targets = [
    'calculateButton',
    'calculateByDateButton',
    'targetDateInput',
    'calculateLoading',
    'analysisSection',
    'salesSnapshotBody',
    'salesChangeTitle',
    'addedSalesProjectsBody',
    'returnSnapshotBody',
    'returnChangeTitle',
    'addedReturnProjectsBody',
    'averageReturnRate',
    'totalSalesAmount',
    'lossRate',
    'totalSalesPreview',
    'totalSalesPreviewValue'
  ]

  static values = {
    calculateUrl: String
  }

  calculate() {
    this.fetchStatistics(null);
  }

  calculateByDate() {
    const targetDate = this.targetDateInputTarget.value;
    if (!targetDate) {
      alert('日付を入力してください');
      return;
    }

    this.fetchStatistics(targetDate);
  }

  fetchStatistics(targetDate) {
    this.setButtonsDisabled(true);
    this.calculateLoadingTarget.style.display = 'inline';

    let url = this.calculateUrlValue;
    if (targetDate) {
      url += `?target_date=${encodeURIComponent(targetDate)}`;
    }

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      }
    })
      .then(response => {
        if (!response.ok) {
          return response.json().then(data => {
            throw new Error(data.error || '計算に失敗しました');
          });
        }
        return response.json();
      })
      .then(data => {
        this.averageReturnRateTarget.value = data.average_return_rate;
        this.totalSalesAmountTarget.value = data.total_sales_amount;
        this.lossRateTarget.value = data.loss_rate;

        this.highlightUpdatedFields();
        this.updateTotalSalesPreview();

        // タブ付き分析セクションを表示
        this.analysisSectionTarget.style.display = 'block';

        // タブ1: 累計販売実績
        if (data.sales_snapshots) {
          this.renderSalesSnapshots(data.sales_snapshots);
        }
        if (data.sales_change_analysis) {
          this.renderSalesChangeAnalysis(data.sales_change_analysis);
        }

        // タブ2: 平均リターン
        if (data.return_snapshots) {
          this.renderReturnSnapshots(data.return_snapshots);
        }
        if (data.return_change_analysis) {
          this.renderReturnChangeAnalysis(data.return_change_analysis);
        }

        this.setButtonsDisabled(false);
        this.calculateLoadingTarget.style.display = 'none';
      })
      .catch(error => {
        alert('計算に失敗しました: ' + error.message);
        this.setButtonsDisabled(false);
        this.calculateLoadingTarget.style.display = 'none';
      });
  }

  setButtonsDisabled(disabled) {
    this.calculateButtonTarget.disabled = disabled;
    this.calculateByDateButtonTarget.disabled = disabled;
  }

  highlightUpdatedFields() {
    const fields = [
      this.averageReturnRateTarget,
      this.totalSalesAmountTarget,
      this.lossRateTarget
    ];

    fields.forEach(field => {
      field.style.backgroundColor = '#fff3cd';
      field.style.transition = 'background-color 0.3s ease';

      setTimeout(() => {
        field.style.backgroundColor = '';
      }, 3000);
    });
  }

  updateTotalSalesPreview() {
    const amount = parseInt(this.totalSalesAmountTarget.value, 10) || 0;

    if (amount > 0) {
      const okuValue = this.formatOku(amount);
      this.totalSalesPreviewValueTarget.textContent = okuValue;
      this.totalSalesPreviewTarget.style.display = 'block';
    } else {
      this.totalSalesPreviewTarget.style.display = 'none';
    }
  }

  // ============================================================
  // タブ1: 累計販売実績
  // ============================================================

  renderSalesSnapshots(snapshots) {
    this.salesSnapshotBodyTarget.innerHTML = '';

    snapshots.forEach(snapshot => {
      const row = document.createElement('tr');

      const monthCell = document.createElement('td');
      monthCell.textContent = snapshot.month;
      row.appendChild(monthCell);

      const salesCell = document.createElement('td');
      salesCell.textContent = this.formatYen(snapshot.total_sales_amount) + this.formatDiff(snapshot.diff_total_sales_amount, v => this.formatYen(v));
      row.appendChild(salesCell);

      const countCell = document.createElement('td');
      countCell.textContent = snapshot.project_count + '件';
      row.appendChild(countCell);

      this.salesSnapshotBodyTarget.appendChild(row);
    });
  }

  renderSalesChangeAnalysis(analysis) {
    this.salesChangeTitleTarget.textContent = `変動要因（${analysis.previous_month} → ${analysis.current_month}）`;

    const projects = analysis.added_sales_projects;
    this.addedSalesProjectsBodyTarget.innerHTML = '';

    if (projects.length === 0) {
      this.renderEmptyRow(this.addedSalesProjectsBodyTarget, 3, '新規対象プロジェクトはありません');
      return;
    }

    let totalSales = 0;

    projects.forEach(project => {
      const row = document.createElement('tr');
      this.appendCell(row, project.name);
      this.appendCell(row, project.operation_start_date || '—');
      this.appendCell(row, this.formatYen(project.sales_amount));
      this.addedSalesProjectsBodyTarget.appendChild(row);

      totalSales += project.sales_amount;
    });

    // 合計行
    const totalRow = document.createElement('tr');
    totalRow.classList.add('font-weight-bold');
    this.appendCell(totalRow, `合計（${projects.length}件）`);
    this.appendCell(totalRow, '');
    this.appendCell(totalRow, this.formatYen(totalSales));
    this.addedSalesProjectsBodyTarget.appendChild(totalRow);
  }

  // ============================================================
  // タブ2: 平均リターン
  // ============================================================

  renderReturnSnapshots(snapshots) {
    this.returnSnapshotBodyTarget.innerHTML = '';

    snapshots.forEach(snapshot => {
      const row = document.createElement('tr');

      const monthCell = document.createElement('td');
      monthCell.textContent = snapshot.month;
      row.appendChild(monthCell);

      const rateCell = document.createElement('td');
      rateCell.textContent = snapshot.average_return_rate.toFixed(3) + '%' + this.formatDiffRate(snapshot.diff_average_return_rate);
      row.appendChild(rateCell);

      const wtCell = document.createElement('td');
      wtCell.textContent = this.formatYenDetail(snapshot.weighted_total) + this.formatDiff(snapshot.diff_weighted_total, v => this.formatYenDetail(v));
      row.appendChild(wtCell);

      const salesCell = document.createElement('td');
      salesCell.textContent = this.formatYenDetail(snapshot.sales_amount) + this.formatDiff(snapshot.diff_sales_amount, v => this.formatYenDetail(v));
      row.appendChild(salesCell);

      const countCell = document.createElement('td');
      countCell.textContent = snapshot.project_count + '件';
      row.appendChild(countCell);

      this.returnSnapshotBodyTarget.appendChild(row);
    });
  }

  renderReturnChangeAnalysis(analysis) {
    this.returnChangeTitleTarget.textContent = `変動要因（${analysis.previous_month} → ${analysis.current_month}）`;

    const projects = analysis.added_return_projects;
    this.addedReturnProjectsBodyTarget.innerHTML = '';

    if (projects.length === 0) {
      this.renderEmptyRow(this.addedReturnProjectsBodyTarget, 6, '新規収穫完了プロジェクトはありません');
      return;
    }

    let totalPurchased = 0;
    let totalWeighted = 0;
    let totalContribution = 0;

    projects.forEach(project => {
      const row = document.createElement('tr');
      this.appendCell(row, project.name);
      this.appendCell(row, project.last_transfer_date || '—');
      this.appendCell(row, project.return_rate + '%');
      this.appendCell(row, this.formatYenDetail(project.weighted_contribution));
      this.appendCell(row, this.formatYenDetail(project.purchased_amount));
      this.appendCell(row, this.formatContribution(project.contribution));
      this.addedReturnProjectsBodyTarget.appendChild(row);

      totalPurchased += project.purchased_amount;
      totalWeighted += project.weighted_contribution;
      totalContribution += project.contribution;
    });

    // 新規合計行
    const newTotalRow = document.createElement('tr');
    newTotalRow.classList.add('font-weight-bold');
    this.appendCell(newTotalRow, `合計（${projects.length}件）`);
    this.appendCell(newTotalRow, '');
    this.appendCell(newTotalRow, '—');
    this.appendCell(newTotalRow, this.formatYenDetail(totalWeighted));
    this.appendCell(newTotalRow, this.formatYenDetail(totalPurchased));
    this.appendCell(newTotalRow, this.formatContribution(totalContribution));
    this.addedReturnProjectsBodyTarget.appendChild(newTotalRow);
  }

  appendCell(row, text) {
    const cell = document.createElement('td');
    cell.textContent = text;
    row.appendChild(cell);
  }

  // ============================================================
  // 共通ヘルパー
  // ============================================================

  // 寄与度の表示（小数3桁、微小値は <±0.001% と表示）
  formatContribution(value) {
    if (value === 0) return '0%';

    const rounded = Math.round(value * 1000) / 1000;
    if (rounded === 0) {
      return value > 0 ? '<+0.001%' : '<-0.001%';
    }

    const sign = rounded >= 0 ? '+' : '';
    return `${sign}${rounded.toFixed(3)}%`;
  }

  formatOku(amount) {
    const OKU_YEN = 100000000;
    const valueInOku = amount / OKU_YEN;

    if (valueInOku >= 100) {
      return Math.round(valueInOku).toString();
    }
    return valueInOku.toFixed(1);
  }

  // 万円レベルまで表示（例: 3億1,234万円、1,234万円）
  formatYenDetail(amount) {
    const absAmount = Math.abs(amount);
    const sign = amount < 0 ? '-' : '';

    if (absAmount >= 100000000) {
      const oku = Math.floor(absAmount / 100000000);
      const man = Math.round((absAmount % 100000000) / 10000);
      if (man === 0) {
        return `${sign}${oku}億円`;
      }
      return `${sign}${oku}億${man.toLocaleString()}万円`;
    } else if (absAmount >= 10000) {
      return `${sign}${Math.round(absAmount / 10000).toLocaleString()}万円`;
    }
    return `${sign}${absAmount.toLocaleString()}円`;
  }

  formatYen(amount) {
    const abs = Math.abs(amount);
    const sign = amount < 0 ? '-' : '';

    if (abs >= 100000000) {
      const oku = Math.floor(abs / 100000000);
      const man = Math.round((abs % 100000000) / 10000);
      if (man === 0) {
        return `${sign}${oku}億円`;
      }
      return `${sign}${oku}億${man.toLocaleString()}万円`;
    } else if (abs >= 10000) {
      return `${sign}${Math.round(abs / 10000).toLocaleString()}万円`;
    }
    return `${sign}${abs.toLocaleString()}円`;
  }

  formatDiff(value, formatter) {
    if (value === undefined || value === null) return '';
    const sign = value >= 0 ? '+' : '';
    return ` (${sign}${formatter(value)})`;
  }

  formatDiffRate(value) {
    if (value === undefined || value === null) return '';
    const sign = value >= 0 ? '+' : '';
    return ` (${sign}${value.toFixed(3)}%)`;
  }

  renderEmptyRow(tbody, colspan, message) {
    const row = document.createElement('tr');
    const cell = document.createElement('td');
    cell.setAttribute('colspan', colspan.toString());
    cell.classList.add('text-muted', 'text-center');
    cell.textContent = message;
    row.appendChild(cell);
    tbody.appendChild(row);
  }
}

export { SiteStatisticsController };
